import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { firestore as db } from "../backend/firebaseconfig";
import { useUserRole } from "./UserRoleContext";
import { useUserEmail } from "./UserEmailContext";

const UserPermissionsContext = createContext();

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const normaliseEmail = (value = "") => String(value).trim().toLowerCase();

const getPermissionsFromUserData = (userData = {}) => {
  const permissions = [
    ...asArray(userData.permissions),
    ...asArray(userData.userPermissions),
    ...asArray(userData.permission),
    ...asArray(userData.access),
    ...asArray(userData.status),
    ...asArray(userData.statuses),
    ...asArray(userData.category),
    ...asArray(userData.categories),
    ...(userData.canViewPayments === true ? ["Can View Payments"] : []),
    ...(userData.canViewGroupPayments === true
      ? ["Can View Group Payments"]
      : []),
  ];

  return Array.from(
    new Set(permissions.map((item) => String(item).trim()).filter(Boolean)),
  );
};

export const UserPermissionsProvider = ({ children }) => {
  const [userPermissions, setUserPermissions] = useState(() => {
    try {
      const storedPermissions = localStorage.getItem("userPermissions");
      return storedPermissions ? JSON.parse(storedPermissions) : [];
    } catch (error) {
      return [];
    }
  });

  const { userRole } = useUserRole();
  const { userEmail } = useUserEmail();

  useEffect(() => {
    if (!userRole || !userEmail) {
      setUserPermissions([]);
      localStorage.removeItem("userPermissions");
      return;
    }

    const safeEmail = normaliseEmail(userEmail);

    const isAdministrator =
      String(userRole).trim().toLowerCase() === "administrators";
    const userRef = isAdministrator
      ? collection(db, "Authorized Members", userRole, "Data")
      : doc(db, "Authorized Members", userRole, "Data", safeEmail);

    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        let userData;
        if (isAdministrator) {
          const matches = snapshot.docs.filter((userSnap) => {
            const data = userSnap.data() || {};
            return [userSnap.id, data.email, data.emailAddress]
              .map(normaliseEmail)
              .some((email) => email === safeEmail);
          });
          userData = matches.length === 1 ? matches[0].data() : null;
        } else {
          userData = snapshot.exists() ? snapshot.data() : null;
        }

        if (!userData) {
          setUserPermissions([]);
          localStorage.removeItem("userPermissions");
          return;
        }

        const permissions = getPermissionsFromUserData(userData);

        setUserPermissions(permissions);
        localStorage.setItem("userPermissions", JSON.stringify(permissions));
      },
      (error) => {
        console.error("Failed to listen to user permissions:", error);
        setUserPermissions([]);
        localStorage.removeItem("userPermissions");
      },
    );

    return () => unsubscribe();
  }, [userRole, userEmail]);

  return (
    <UserPermissionsContext.Provider
      value={{ userPermissions, setUserPermissions }}
    >
      {children}
    </UserPermissionsContext.Provider>
  );
};

export const useUserPermissions = () => {
  const context = useContext(UserPermissionsContext);

  if (!context) {
    throw new Error(
      "useUserPermissions must be used within a UserPermissionsProvider",
    );
  }

  return context;
};
